import { useForm } from "react-hook-form";
import type { MutateOptions } from "react-query";
import type {
  OpenCallSubmission,
  OpenCallSubmissionFormFields,
  OpenCallSubmissionPostBody,
} from "../types";
import { useCreateSubmission } from "./useCreateSubmission";
import { useUpdateSubmission } from "./useUpdateSubmission";

type UseSubmissionFormProps = {
  openCallId: string;
  submission?: OpenCallSubmission;
} & MutateOptions<OpenCallSubmission, unknown, OpenCallSubmissionPostBody>;

/**
 * Only used to validate form submission, stripped before sending to API
 */
type SubmissionFormCheckboxes = {
  affirmOriginal: boolean;
  affirmUsage: boolean;
};

const useSubmissionForm = ({
  openCallId,
  submission,
  ...mutateOptions
}: UseSubmissionFormProps) => {
  const { handleSubmit, ...form } = useForm<
    OpenCallSubmissionFormFields & SubmissionFormCheckboxes
  >({ defaultValues: submission ?? {} });

  const createMutation = useCreateSubmission();
  const updateMutation = useUpdateSubmission();

  const isEdit = !!submission;
  const mutation = isEdit ? updateMutation : createMutation;

  const onSubmit = handleSubmit(
    ({ affirmOriginal, affirmUsage, ...formData }) => {
      // NOTE: this removes checkbox values that were used to agree to terms for validation

      // Transform required field values to request data if necessary
      // Add optional fields if they exist, otherwise omit from request body

      mutation.mutate(
        {
          openCallSubmission: isEdit
            ? { ...submission, ...formData }
            : formData,
          openCallId,
          submissionId: submission?.submissionId,
        },
        mutateOptions
      );
    }
  );

  return {
    ...form,
    isEdit,
    mutation,
    onSubmit,
  };
};

export { useSubmissionForm };
